import React, { useState, useEffect, useMemo } from "react"
import { observer } from "mobx-react-lite"
import { useSafeAreaInsets } from "~/ui/safe-area"
import styled, { useTheme } from "~/ui/styled"
import { ScrollView, FlatList } from "~/ui/primitives"
import dayjs from "dayjs"
import isoWeek from "dayjs/plugin/isoWeek"
import {
  StartFlex,
  ColumnFlex,
  ColumnCenterFlex,
  CenterFlex,
  Text,
  BetweenFlex,
  IconFont,
  IconButton,
  Button,
  Grid,
} from "~components/El"
import { useStores } from "~models"
import { usePopup } from "~hooks/usePopup"
import { openDatePickerInBottomPopup } from "~components/DatePicker"
import { globalToast, globalSpinner, truncateDecimal } from "~utils"
import { flexPageContainer, scrollViewFillStyle } from "~/ui/scroll-layout"
dayjs.extend(isoWeek)

const AGENT_PAGE_SIZE = 5
const STAT_GREEN = "#07c160"
const STAT_RED = "#fa5251"
const STAT_BLUE = "#4e98f3"
const STAT_PURPLE = "#9b59b6"
export const REVENUE_SINGLE_WIDTH = 400
export const REVENUE_DUAL_WIDTH = 820
const LEFT_PANE_WIDTH = 360

const fmt = "YYYY-MM-DD"
const displayDateFmt = "YYYY/MM/DD"

const formatStatValue = (
  value: string | number | null | undefined,
  options?: { withPlus?: boolean }
) => {
  if (value == null || value === "") return "0"
  const num = Number(value)
  if (Number.isNaN(num)) return String(value)
  const raw = String(value)
  if (options?.withPlus && num > 0 && !raw.startsWith("+")) return `+${raw}`
  return raw
}
const options = [
  {
    label: "今天",
    value: `${dayjs().startOf("day").format(fmt)}~${dayjs().endOf("day").format(fmt)}`,
    start_date: dayjs().startOf("day").format(fmt),
    end_date: dayjs().endOf("day").format(fmt),
  },
  {
    label: "本周",
    value: `${dayjs().startOf("isoWeek").format(fmt)}~${dayjs().endOf("isoWeek").format(fmt)}`,
    start_date: dayjs().startOf("isoWeek").format(fmt),
    end_date: dayjs().endOf("isoWeek").format(fmt),
  },
  {
    label: "本月",
    value: `${dayjs().startOf("month").format(fmt)}~${dayjs().endOf("month").format(fmt)}`,
    start_date: dayjs().startOf("month").format(fmt),
    end_date: dayjs().endOf("month").format(fmt),
  },
  {
    label: "自定义",
    value: `${dayjs().startOf("day").format(fmt)}~${dayjs().endOf("day").format(fmt)}`,
    start_date: dayjs().startOf("day").format(fmt),
    end_date: dayjs().endOf("day").format(fmt),
  },
]
const Title = ({ icon = "", iconColor = "", title = "" }) => {
  return (
    <ColumnFlex width="100%">
      <StartFlex gap="6px" py={12}>
        <IconFont name={icon} color={iconColor} size={16} />
        <Text color={"text"} fontSize={"14px"} fontWeight="600">
          {title}
        </Text>
      </StartFlex>
      <CenterFlex height="1px" width="100%" bg="#e3e6ec" />
    </ColumnFlex>
  )
}

const Col = ({
  color = "",
  value = "",
  label = "",
  size = "20px",
  gap = "5px",
  labelSize = "14px",
  ...props
}) => {
  return (
    <ColumnCenterFlex width="100%" height="42px" gap={gap} {...props}>
      <Text color={color} fontSize={size} fontWeight="600">
        {value}
      </Text>
      <Text color={"secondaryText"} fontSize={labelSize}>
        {label}
      </Text>
    </ColumnCenterFlex>
  )
}

const customOption = options.find((o) => o.label === "自定义")!

const FilterTabRow = styled(StartFlex)`
  width: 100%;
  gap: 8px;
  padding: 12px 16px 0;
`

const FilterTab = styled(Button)<{ $active?: boolean }>`
  flex: 1;
  height: 32px;
  border-radius: 16px;
  background-color: ${({ $active }) => ($active ? STAT_GREEN : "#f2f3f5")};
`

const DateFieldButton = styled(Button)`
  flex: 1;
  height: 36px;
  border-radius: 8px;
  background-color: #f2f3f5;
  padding: 0 12px;
`

const DualColumnLayout = styled(StartFlex)`
  flex: 1;
  min-height: 0;
  width: 100%;
  height: 100%;
  align-items: stretch;
  align-self: stretch;
`

const PopupHeader = styled(ColumnFlex)`
  flex-shrink: 0;
  width: 100%;
  align-self: stretch;
  min-height: 52px;
  justify-content: center;
  padding: 8px 0;
  background-color: ${({ theme }) => theme.colors.blockBackground};
`

const LeftPane = styled(ColumnFlex)`
  width: ${LEFT_PANE_WIDTH}px;
  flex: 0 0 ${LEFT_PANE_WIDTH}px;
  height: 100%;
  min-height: 0;
  align-items: stretch;
  border-right-width: 1px;
  border-right-color: #e3e6ec;
  border-right-style: solid;
  background-color: ${({ theme }) => theme.colors.blockBackground};
  overflow: hidden;
`

const RightPane = styled(ColumnFlex)`
  flex: 1;
  min-width: 0;
  min-height: 0;
  height: 100%;
  background-color: ${({ theme }) => theme.colors.blockBackground};
  overflow: hidden;
`

const RightPanelHeader = styled(CenterFlex)`
  flex-shrink: 0;
  min-height: 48px;
  border-bottom-width: 1px;
  border-bottom-color: #e3e6ec;
  border-bottom-style: solid;
`

const SearchBarWrap = styled(StartFlex)`
  flex-shrink: 0;
  width: calc(100% - 32px);
  height: 36px;
  margin: 12px 16px 0;
  padding: 0 12px;
  border-radius: 18px;
  background-color: #f2f3f5;
  align-items: center;
  gap: 8px;
`

const PaginationBar = styled(BetweenFlex)`
  flex-shrink: 0;
  padding: 10px 16px;
  border-top-width: 1px;
  border-top-color: #e3e6ec;
  border-top-style: solid;
  align-items: center;
`

const PageNavButton = styled(Button)<{ $disabled?: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background-color: #f2f3f5;
  opacity: ${({ $disabled }) => ($disabled ? 0.4 : 1)};
`

const Filters: React.FC<any> = (props) => {
  const theme = useTheme()
  const popup = usePopup()
  const { current, onChange } = props
  const maxSelectable = useMemo(() => dayjs().endOf("day").toDate(), [])
  const parseRangeDate = (s: string) => dayjs(s, fmt).toDate()
  const formatDisplayDate = (s: string) => (s ? dayjs(s).format(displayDateFmt) : "年/月/日")

  return (
    <ColumnFlex width="100%" height="auto">
      <FilterTabRow>
        {options.map((item, index) => {
          const active = current.label === item.label
          return (
            <FilterTab
              key={index}
              $active={active}
              onClick={() => onChange(item)}
            >
              <Text color={active ? "#fff" : "secondaryText"} fontSize="13px">
                {item.label}
              </Text>
            </FilterTab>
          )
        })}
      </FilterTabRow>
      {current.label === "自定义" && (
        <BetweenFlex width="100%" padding="10px 16px 12px" gap="10px" alignItems="center">
          <DateFieldButton
            onClick={() => {
              openDatePickerInBottomPopup(popup, {
                title: "开始时间",
                value: parseRangeDate(current.start_date),
                maximumDate: dayjs(current.end_date).endOf("day").toDate(),
                onConfirm: (d) => {
                  const start = dayjs(d).format(fmt)
                  const endRaw = current.end_date
                  const end = dayjs(endRaw).isBefore(dayjs(d)) ? start : endRaw
                  onChange({
                    ...customOption,
                    start_date: start,
                    end_date: end,
                    value: `${start}~${end}`,
                  })
                },
              })
            }}
          >
            <BetweenFlex width="100%" alignItems="center">
              <Text color="secondaryText" fontSize="13px">
                {formatDisplayDate(current.start_date)}
              </Text>
              <IconFont name="shijian" color={theme.colors.secondaryText} size={14} />
            </BetweenFlex>
          </DateFieldButton>
          <Text color="secondaryText" fontSize="13px">
            至
          </Text>
          <DateFieldButton
            onClick={() => {
              openDatePickerInBottomPopup(popup, {
                title: "结束时间",
                value: parseRangeDate(current.end_date),
                minimumDate: dayjs(current.start_date).startOf("day").toDate(),
                maximumDate: maxSelectable,
                onConfirm: (d) => {
                  const end = dayjs(d).format(fmt)
                  const startRaw = current.start_date
                  const start = dayjs(d).isBefore(dayjs(startRaw)) ? end : startRaw
                  onChange({
                    ...customOption,
                    start_date: start,
                    end_date: end,
                    value: `${start}~${end}`,
                  })
                },
              })
            }}
          >
            <BetweenFlex width="100%" alignItems="center">
              <Text color="secondaryText" fontSize="13px">
                {formatDisplayDate(current.end_date)}
              </Text>
              <IconFont name="shijian" color={theme.colors.secondaryText} size={14} />
            </BetweenFlex>
          </DateFieldButton>
        </BetweenFlex>
      )}
    </ColumnFlex>
  )
}

const TextInput = styled.TextInput`
  flex: 1;
  padding: 0 10px;
`
const SubordinateItem: React.FC<any> = ({ item }) => {
  return (
    <ColumnCenterFlex
      width="100%"
      height="84px"
      bg="background"
      borderRadius="10px"
      padding="10px"
      mb="10px"
    >
      <BetweenFlex width="100%">
        <Text color={"text"} fontSize={"14px"}>
          {item.user_name}
        </Text>
        <Text color={STAT_GREEN} fontSize="14px" fontWeight="600">
          +{item.agent_rebate_amount}佣金
        </Text>
      </BetweenFlex>
      <Grid columns={4} gap={10}>
        <Col size="14px" labelSize="12px" label="流水" value={item.bet_amount} color="#000" />
        <Col size="14px" labelSize="12px" label="中奖" value={item.payout_amount && Number(item.payout_amount) > 0 ? `+${item.payout_amount}` : item.payout_amount} color="#07c160" />
        <Col size="14px" labelSize="12px" label="输赢" value={item.net_amount && Number(item.net_amount) > 0 ? `+${item.net_amount}` : item.net_amount} color="#fa5251" />
        <Col size="14px" labelSize="12px" label="返水" value={`${truncateDecimal(Number(item.member_rebate_amount) + Number(item.net_loss_rebate_amount), 2)}`} color="#4e98f3" />
      </Grid>
    </ColumnCenterFlex>
  )
}

const AdminMemberItem: React.FC<any> = ({ item }) => {
  // const theme = useTheme()
  const ColorMap = useMemo(() => {
    if (item.chat_member?.is_agent) {
      return {
        color: "#c332d0",
        bgColor: "#eea4f5",
        label: "代理",
      }
    } else if (item.chat_member?.is_collect_bot) {
      return {
        color: "#fa5251",
        bgColor: "#fb9494",
        label: "抢包机器人",
      }
    } else if (item.chat_member?.is_compensate_bot) {
      return {
        color: "#4e98f3",
        bgColor: "#90bef6",
        label: "赔付机器人",
      }
    } else {
      return {  
        color: "#363535",
        bgColor: "#c2c2c2",
        label: "成员",
      }
    }
  }, [item.chat_member])
  return (
    <ColumnCenterFlex
      width="100%"
      height="84px"
      bg="background"
      borderRadius="10px"
      padding="10px"
      mb="10px"
    >
      <BetweenFlex width="100%">
        <Text color={"text"} fontSize={"14px"}>
          {item.user_name || item.username }
        </Text>
        <CenterFlex width='auto' bg={ColorMap.bgColor} borderRadius="10px" padding="2px 5px">
          <Text color={ColorMap.color} fontSize={"10px"}>{ColorMap.label}</Text>
        </CenterFlex>
      </BetweenFlex>
      <Grid columns={4} gap={10}>
        <Col size="14px" labelSize="12px" label="流水" value={item.bet_amount} color="#000" />
        <Col
          size="14px"
          labelSize="12px"
          label="中奖"
          value={
            item.payout_amount && Number(item.payout_amount) > 0 ? `+${item.payout_amount}` : item.payout_amount
          }
          color="#07c160"
        />
        <Col
          size="14px"
          labelSize="12px"
          label="输赢"
          value={item.net_amount && Number(item.net_amount) > 0 ? `+${item.net_amount}` : item.net_amount}
          color="#fa5251"
        />
        <Col
          size="14px"
          labelSize="12px"
          label="返水"
          value={item.total_rebate ?? item.rebate_amount ?? item.member_rebate_amount}
          color="#4e98f3"
        />
      </Grid>
    </ColumnCenterFlex>
  )
}

const PopupHeaderBar: React.FC<{
  displayName: string
  onClose?: () => void
  theme: ReturnType<typeof useTheme>
}> = ({ displayName, onClose, theme }) => (
  <PopupHeader>
    <BetweenFlex width="100%" alignItems="center" padding="0 4px">
      {onClose ? (
        <IconButton
          name="left"
          size={40}
          iconSize={16}
          iconColor={theme.colors.text}
          onClick={onClose}
        />
      ) : (
        <CenterFlex width="40px" />
      )}
      <ColumnCenterFlex flex={1} minWidth={0} padding="0 8px">
        <Text color="text" fontWeight="600" fontSize="16px" textAlign="center">
          收益统计
        </Text>
        {displayName ? (
          <Text color="secondaryText" fontSize="12px" numberOfLines={1} textAlign="center">
            {displayName}
          </Text>
        ) : null}
      </ColumnCenterFlex>
      <CenterFlex width="40px" />
    </BetweenFlex>
  </PopupHeader>
)

const BasicStatsSection: React.FC<{ data: any }> = ({ data }) => (
  <ColumnFlex width="100%" padding="0 16px">
    <Title title="基础统计" icon="jctj" iconColor={STAT_GREEN} />
    <Grid columns={2} padding="10px 0" gap={10}>
      <Col label="总流水" value={data?.user_basic_stats?.total_bet_amount} color="#000" />
      <Col
        label="总中奖"
        value={formatStatValue(data?.user_basic_stats?.total_payout_amount, { withPlus: true })}
        color={STAT_GREEN}
      />
      <Col label="总输赢" value={data?.user_basic_stats?.total_win_loss} color={STAT_RED} />
      <Col label="总返水" value={data?.user_basic_stats?.total_rebate} color={STAT_BLUE} />
    </Grid>
  </ColumnFlex>
)

const RedPacketStatsSection: React.FC<{ data: any }> = ({ data }) => (
  <ColumnFlex width="100%" padding="0 16px">
    <Title title="红包统计" icon="Frame-1" iconColor={STAT_RED} />
    <Grid columns={2} padding="10px 0 16px" gap={10}>
      <Col label="总流水" value={data?.user_basic_stats?.total_bets} color={STAT_RED} />
      <Col label="领取红包" value={data?.user_basic_stats?.total_payout_count} color={STAT_GREEN} />
    </Grid>
  </ColumnFlex>
)

const AgentSubordinateStatsSection: React.FC<{ data: any }> = ({ data }) => (
  <ColumnFlex width="100%" padding="0 16px 12px">
    <Title title="下级数据" icon="cjql" iconColor={STAT_BLUE} />
    <Grid columns={2} padding="10px 0" gap={10}>
      <Col label="下级总数" value={data?.chat_agent_stats?.total_members} color={STAT_PURPLE} />
      <Col label="今日活跃" value={data?.chat_agent_stats?.active_members} color={STAT_GREEN} />
      <Col
        label="总流水"
        value={formatStatValue(data?.chat_agent_stats?.total_bet_amount, { withPlus: true })}
        color="#000"
      />
      <Col
        label="总中奖"
        value={formatStatValue(data?.chat_agent_stats?.total_payout_amount, { withPlus: true })}
        color={STAT_GREEN}
      />
      <Col label="总输赢" value={data?.chat_agent_stats?.total_win_loss} color={STAT_RED} />
      <Col label="总返水" value={data?.chat_agent_stats?.total_rebate} color={STAT_BLUE} />
    </Grid>
  </ColumnFlex>
)

const AgentSubordinatePanel: React.FC<{
  title: string
  searchPlaceholder: string
  list: any[]
  groupName: string
  onSearchChange: (text: string) => void
  page: number
  totalPages: number
  onPageChange: (page: number) => void
  renderItem: (item: any) => React.ReactNode
}> = ({
  title,
  searchPlaceholder,
  list,
  groupName,
  onSearchChange,
  page,
  totalPages,
  onPageChange,
  renderItem,
}) => (
  <ColumnFlex flex={1} minHeight={0} width="100%" height="100%">
    <RightPanelHeader>
      <Text color="text" fontWeight="600" fontSize="15px">
        {title}
      </Text>
    </RightPanelHeader>
    <SearchBarWrap>
      <IconFont name="ss" color="secondaryText" size={16} />
      <TextInput placeholder={searchPlaceholder} value={groupName} onChangeText={onSearchChange} />
    </SearchBarWrap>
    <FlatList
      data={list}
      keyExtractor={(item, index) => String(item?.user_id ?? item?.id ?? index)}
      renderItem={({ item }) => renderItem(item)}
      style={scrollViewFillStyle}
      contentContainerStyle={{
        flexGrow: 1,
        paddingHorizontal: 16,
        paddingTop: 12,
        paddingBottom: 8,
      }}
    />
    <PaginationBar>
      <Text color="secondaryText" fontSize="13px">
        第 {page} 页，共 {totalPages} 页
      </Text>
      <StartFlex gap="8px" width="auto" alignItems="center">
        <PageNavButton $disabled={page <= 1} onClick={() => page > 1 && onPageChange(page - 1)}>
          <IconFont name="left" color="secondaryText" size={12} />
        </PageNavButton>
        <Text color="text" fontSize="13px" fontWeight="600">
          {page}
        </Text>
        <PageNavButton
          $disabled={page >= totalPages}
          onClick={() => page < totalPages && onPageChange(page + 1)}
        >
          <IconFont name="right" color="secondaryText" size={12} />
        </PageNavButton>
      </StartFlex>
    </PaginationBar>
  </ColumnFlex>
)

const Container = styled.View<{ $compact?: boolean }>`
  background-color: ${({ theme }) => theme.colors.blockBackground};
  width: 100%;
  ${({ $compact }) => ($compact ? "" : flexPageContainer)}
`

const PopupRoot = styled(ColumnFlex)<{ $compact?: boolean }>`
  flex: ${({ $compact }) => ($compact ? "0 1 auto" : "1")};
  min-height: 0;
  width: 100%;
  height: ${({ $compact }) => ($compact ? "auto" : "100%")};
  max-height: 100%;
  background-color: ${({ theme }) => theme.colors.blockBackground};
  overflow: hidden;
`

type RevenueStatisticsProps = {
  route?: {
    params: {
      chat_id: number | string
      isCurrentUserRole?: string
      item?: { name?: string }
    }
  }
  chat_id?: number | string
  isCurrentUserRole?: string
  chatName?: string
  onClose?: () => void
}

const RevenueStatistics: React.FC<RevenueStatisticsProps> = ({
  route,
  chat_id: chatIdProp,
  isCurrentUserRole: roleProp,
  chatName,
  onClose,
}) => {
  const theme = useTheme()
  const insets = useSafeAreaInsets()
  const chat_id = chatIdProp ?? route?.params?.chat_id
  const isCurrentUserRole = roleProp ?? route?.params?.isCurrentUserRole
  const displayName = chatName ?? route?.params?.item?.name ?? ""
  const isPopup = Boolean(onClose)
  const listBottomPadding = isPopup ? 16 : insets.bottom + 60
  // console.log("isCurrentUserRole", isCurrentUserRole);
  const { apiStore } = useStores()
  const [current, setCurrent] = useState(options[0])
  const [data, setData] = useState<any>({})
  const [groupName, setGroupName] = useState("")
  const [list, setList] = useState<any>([])
  const [detailPage, setDetailPage] = useState(1)
  const isAgent = isCurrentUserRole === "AGENT"
  const isAdmin = isCurrentUserRole === "ADMIN"
  const hasDetailPanel = isAgent || isAdmin

  const getStatisticsData = async () => {
    try {
      globalSpinner.show()
      const res = await apiStore.getGroupChatDataStatistics({
        chat_id: chat_id,
        start_date: current?.start_date,
        end_date: current?.end_date,
      })
      if (res.data) {
        setData(res.data)
      }
      globalSpinner.close()
    } catch (error) {
      globalSpinner.close()
      globalToast.show(error?.message || "获取统计数据失败")
    }
  }

  const getDataList = async () => {
    if (!isAdmin) return
    try {
      const res = await apiStore.getGroupChatDataList({
        chat_id: chat_id,
        page: 1,
        per_page: 200,
        start_date: current?.start_date,
        end_date: current?.end_date,
      })
      if (res?.data) {
        const payload = res.data
        if (Array.isArray(payload)) {
          setList(payload)
        } else {
          const rows = payload.list ?? payload.data ?? payload.records ?? []
          setList(Array.isArray(rows) ? rows : [])
        }
      }
    } catch (error) {
    }
  }

  useEffect(() => {
    getStatisticsData()
    if (isAdmin) {
      getDataList()
    }
  }, [current])

  useEffect(() => {
    setDetailPage(1)
  }, [groupName, current])

  const agentList = (data?.chat_agent_stats?.user_list || []) as any[]
  const filteredAgentList = agentList.filter((u) => {
    if (!groupName) return true
    const kw = groupName.trim()
    const name = String(u?.user_name || "")
    return name.includes(kw)
  })

  const adminList = (list || []) as any[]
  const filteredAdminList = adminList.filter((u) => {
    if (!groupName) return true
    const kw = groupName.trim()
    const name = String(u?.user_name || u?.username || "")
    const id = String(u?.user_id || u?.id || "")
    return name.includes(kw) || id.includes(kw)
  })

  const detailSourceList = isAgent ? filteredAgentList : filteredAdminList
  const detailTotalPages = Math.max(1, Math.ceil(detailSourceList.length / AGENT_PAGE_SIZE))
  const safeDetailPage = Math.min(detailPage, detailTotalPages)
  const pagedDetailList = detailSourceList.slice(
    (safeDetailPage - 1) * AGENT_PAGE_SIZE,
    safeDetailPage * AGENT_PAGE_SIZE
  )

  const ordinaryExtraStats = (
    <>
      {["COLLECT_BOT"].includes(isCurrentUserRole) && (
        <ColumnFlex width="100%" padding="0 16px">
          <Title title="机器人统计" icon="Frame-1" iconColor={STAT_RED} />
          <Grid columns={2} padding="10px 0 16px" gap={10}>
            <Col
              label="抢到红包数"
              value={String(Number(data?.chat_robot_stats?.dragon_bot_grab_count) + Number(data?.chat_robot_stats?.tiger_bot_grab_count))}
              color={STAT_RED}
            />
            <Col
              label="抢到金额"
              value={String(Number(data?.chat_robot_stats?.dragon_bot_grab_amount) + Number(data?.chat_robot_stats?.tiger_bot_grab_amount))}
              color={STAT_GREEN}
            />
          </Grid>
        </ColumnFlex>
      )}
      {["COMPENSATE_BOT"].includes(isCurrentUserRole) && (
        <ColumnFlex width="100%" padding="0 16px">
          <Title title="赔付统计" icon="Frame-1" iconColor={STAT_RED} />
          <Grid columns={2} padding="10px 0 16px" gap={10}>
            <Col label="赔付次数" value={data?.chat_robot_stats?.compensate_count} color={STAT_RED} />
            <Col label="赔付金额" value={data?.chat_robot_stats?.compensate_amount} color={STAT_GREEN} />
          </Grid>
        </ColumnFlex>
      )}
    </>
  )

  const adminExtraStats = (
    <ColumnFlex width="100%" padding="0 16px 12px">
      <Title title="群组管理" icon="qlgl" iconColor={STAT_BLUE} />
      <Grid columns={2} padding="10px 0" gap={10}>
        <Col label="总成员数" value={data?.chat_revenue_stats?.total_members} color={STAT_BLUE} />
        <Col label="活跃成员" value={data?.chat_revenue_stats?.active_members} color={STAT_GREEN} />
      </Grid>
      <Title title="群收益统计" icon="qlgl" iconColor={STAT_BLUE} />
      <Grid columns={2} padding="10px 0" gap={10}>
        <Col label="群总流水" value={data?.chat_revenue_stats?.total_bet_amount} color="#000" />
        <Col
          label="群总中奖"
          value={formatStatValue(data?.chat_revenue_stats?.total_payout_amount, { withPlus: true })}
          color={STAT_GREEN}
        />
        <Col label="群总输赢" value={data?.chat_revenue_stats?.total_win_loss} color={STAT_RED} />
        <Col label="群总返水" value={data?.chat_revenue_stats?.total_rebate} color={STAT_BLUE} />
      </Grid>
    </ColumnFlex>
  )

  const leftStatsScroll = (
    <ScrollView
      style={hasDetailPanel || !isPopup ? scrollViewFillStyle : { width: "100%" }}
      contentContainerStyle={{ paddingBottom: hasDetailPanel ? 12 : listBottomPadding }}
      showsVerticalScrollIndicator={false}
    >
      <BasicStatsSection data={data} />
      <RedPacketStatsSection data={data} />
      {isAgent ? <AgentSubordinateStatsSection data={data} /> : null}
      {isAdmin ? adminExtraStats : null}
      {!hasDetailPanel ? ordinaryExtraStats : null}
    </ScrollView>
  )

  const renderDetailPanel = () => (
    <AgentSubordinatePanel
      title={isAgent ? "下级详情" : "成员明细"}
      searchPlaceholder={isAgent ? "搜索联系人" : "搜索用户名"}
      list={pagedDetailList}
      groupName={groupName}
      onSearchChange={setGroupName}
      page={safeDetailPage}
      totalPages={detailTotalPages}
      onPageChange={setDetailPage}
      renderItem={(item) => (isAgent ? <SubordinateItem item={item} /> : <AdminMemberItem item={item} />)}
    />
  )

  const renderDualColumnLayout = (showHeader: boolean) => (
    <DualColumnLayout>
      <LeftPane>
        {showHeader ? (
          <PopupHeaderBar displayName={displayName} onClose={onClose} theme={theme} />
        ) : null}
        <Container style={{ flex: 1, minHeight: 0 }}>
          <Filters current={current} onChange={setCurrent} />
          {leftStatsScroll}
        </Container>
      </LeftPane>
      <RightPane>{renderDetailPanel()}</RightPane>
    </DualColumnLayout>
  )

  const renderSingleColumnBody = () => (
    <Container $compact={isPopup}>
      <Filters current={current} onChange={setCurrent} />
      {leftStatsScroll}
    </Container>
  )

  const body = hasDetailPanel ? renderDualColumnLayout(!isPopup) : renderSingleColumnBody()

  if (!isPopup) {
    return body
  }

  if (hasDetailPanel) {
    return <PopupRoot>{renderDualColumnLayout(true)}</PopupRoot>
  }

  return (
    <PopupRoot $compact>
      <PopupHeaderBar displayName={displayName} onClose={onClose} theme={theme} />
      {renderSingleColumnBody()}
    </PopupRoot>
  )
}

export default observer(RevenueStatistics)
